import { Money } from "@redotech/money";
import { Currency } from "@redotech/money/currencies";
import { CurrencyConverter } from "@redotech/money/exchange";
import * as zExt from "@redotech/rpc/ext";
import { isDef } from "@redotech/util/checks";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { rateTableTextFormatSchema } from "../../conversion/delivery-date/format-delivery-date";
import {
  AmazonServices,
  Carriers,
  DhlEcsServices,
  DhlExpressServices,
  FedexServices,
  ServiceLevel,
  UpsServices,
  UspsServices,
} from "../../fulfillments/fulfillment-carriers-and-services";
import { PriceConstraintSchema } from "./rate-table-constraints";

export const shippingDiscountSchema = z.object({
  amount: z
    .object({
      currency: createEnum(Currency, { enumName: "Currency" }),
      value: z.string(),
    })
    .optional(),
  percentage: z.string().optional(),
});
export type ShippingDiscount = z.infer<typeof shippingDiscountSchema>;

export const shippingDiscountRuleSchema = z.object({
  cartPriceConditions: z
    .object({
      minCartPrice: z.number().min(0).nullable(),
      maxCartPrice: z.number().min(0).nullable(),
      currency: createEnum(Currency, { enumName: "Currency" }),
    })
    .optional(),
  discount: shippingDiscountSchema,
});
export type ShippingDiscountRule = z.infer<typeof shippingDiscountRuleSchema>;

export enum RateTableType {
  FIXED = "fixed",
  DYNAMIC = "dynamic",
}

export enum ConditionalPricingCondition {
  WEIGHT = "weight",
  PRICE = "price",
}

/**
 * Preset options for how to select the rate to show from multiple rates.
 */
export enum DynamicRateSelectionPreset {
  CHEAPEST = "cheapest",
  FASTEST = "fastest",
  CUSTOM = "custom",
}

/**
 * The method to use to select the rate to show from multiple rates.
 */
export enum DynamicRateSelectionMethod {
  CHEAPEST = "cheapest",
  FASTEST = "fastest",
}

export enum CustomDynamicRateSelectionConditionKind {
  DELIVERY_DAYS = "delivery_days",
}

/**
 * Comparison operators that can be used for numeric conditions
 */
export enum NumericComparison {
  LESS_THAN = "less_than",
  LESS_THAN_OR_EQUAL = "less_than_or_equal",
  GREATER_THAN = "greater_than",
  GREATER_THAN_OR_EQUAL = "greater_than_or_equal",
  EQUAL_TO = "equal_to",
}

// Should always be in sync with the data repository's copy of the enum
// `metaflow/flows/mercury_model/src/common/rounding.py`
export enum DynamicRateRounding {
  /**
   * E.g. 1.13 -> 2.00
   */
  NEAREST_WHOLE = "NEAREST_WHOLE",
  /**
   * E.g. 1.13 -> 1.99
   */
  NEAREST_WHOLE_MINUS_ONE = "NEAREST_WHOLE_MINUS_ONE",
  /**
   * E.g. 1.13 -> 1.95
   */
  NEAREST_WHOLE_MINUS_FIVE = "NEAREST_WHOLE_MINUS_FIVE",
  /**
   * E.g. 1.13 -> 1.50
   */
  NEAREST_HALF = "NEAREST_HALF",
  /**
   * E.g. 1.13 -> 1.25
   */
  NEAREST_QUARTER = "NEAREST_QUARTER",
  /**
   * E.g. 1.13 -> 1.20
   */
  NEAREST_TENTH = "NEAREST_TENTH",
  /**
   * E.g. 1.13 -> 1.15
   */
  NEAREST_TWENTIETH = "NEAREST_TWENTIETH",
}

export const dynamicRateRoundingToLabel: Record<DynamicRateRounding, string> = {
  [DynamicRateRounding.NEAREST_WHOLE]: "Nearest whole",
  [DynamicRateRounding.NEAREST_WHOLE_MINUS_ONE]: "Nearest whole (minus one)",
  [DynamicRateRounding.NEAREST_WHOLE_MINUS_FIVE]: "Nearest whole (minus five)",
  [DynamicRateRounding.NEAREST_HALF]: "Nearest half",
  [DynamicRateRounding.NEAREST_QUARTER]: "Nearest quarter",
  [DynamicRateRounding.NEAREST_TENTH]: "Nearest tenth",
  [DynamicRateRounding.NEAREST_TWENTIETH]: "Nearest twentieth",
};

export const dynamicRateRoundingToExample: Record<DynamicRateRounding, string> =
  {
    [DynamicRateRounding.NEAREST_WHOLE]: "1.13 -> 2.00",
    [DynamicRateRounding.NEAREST_WHOLE_MINUS_ONE]: "1.13 -> 1.99",
    [DynamicRateRounding.NEAREST_WHOLE_MINUS_FIVE]: "1.13 -> 1.95",
    [DynamicRateRounding.NEAREST_HALF]: "1.13 -> 1.50",
    [DynamicRateRounding.NEAREST_QUARTER]: "1.13 -> 1.25",
    [DynamicRateRounding.NEAREST_TENTH]: "1.13 -> 1.20",
    [DynamicRateRounding.NEAREST_TWENTIETH]: "1.13 -> 1.15",
  };

const roundingKey = ["0", "1", "2", "3", "4", "5", "6"] as const;
type RoundingKey = (typeof roundingKey)[number];
export function isDynamicRateRoundingServiceCodeSerializedKey(
  key: string,
): key is RoundingKey {
  return roundingKey.includes(key as RoundingKey);
}
export const dynamicRateRoundingToServiceCodeDeserialization: Record<
  (typeof roundingKey)[number],
  DynamicRateRounding
> = {
  // CAUTION: Do not change these constants. If you do, carefully consider
  // backwards compatibility. Adding new ones is fine. Do not remove or modify
  // existing ones.
  ["0"]: DynamicRateRounding.NEAREST_WHOLE,
  ["5"]: DynamicRateRounding.NEAREST_WHOLE_MINUS_ONE,
  ["6"]: DynamicRateRounding.NEAREST_WHOLE_MINUS_FIVE,
  ["1"]: DynamicRateRounding.NEAREST_HALF,
  ["2"]: DynamicRateRounding.NEAREST_QUARTER,
  ["3"]: DynamicRateRounding.NEAREST_TENTH,
  ["4"]: DynamicRateRounding.NEAREST_TWENTIETH,
};

export const ShippingRateLocationSchema = z.object({
  country: z.string(),
  provinces: z.array(z.string()),
});
export type ShippingRateLocation = z.infer<typeof ShippingRateLocationSchema>;

export type Rate = FixedRate | DynamicRate;

const WeightConditionSchema = z.object({
  type: z.literal(ConditionalPricingCondition.WEIGHT),
  minGrams: z.number().min(0),
  maxGrams: z.number().min(0),
});
export type WeightCondition = z.infer<typeof WeightConditionSchema>;

const PriceConditionSchema = z.object({
  type: z.literal(ConditionalPricingCondition.PRICE),
  minPrice: z.number().min(0),
  maxPrice: z.number().min(0),
  currency: z.string(),
});
export type PriceCondition = z.infer<typeof PriceConditionSchema>;

export const RateConditionSchema = z.union([
  WeightConditionSchema,
  PriceConditionSchema,
]);
export type RateCondition = z.infer<typeof RateConditionSchema>;

/**
 * Base schema for all custom rate selection conditions
 */
const BaseCustomDynamicRateSelectionConditionSchema = z.object({
  selectionMethod: createEnum(DynamicRateSelectionMethod, {
    enumName: "DynamicRateSelectionMethod",
  }),
});

/**
 * Delivery days condition schema
 */
const DeliveryDaysConditionSchema_ =
  BaseCustomDynamicRateSelectionConditionSchema.extend({
    kind: z.literal(CustomDynamicRateSelectionConditionKind.DELIVERY_DAYS),
    comparison: createEnum(NumericComparison, {
      enumName: "NumericComparison",
    }),
    threshold: z.number(),
  });
export const DeliveryDaysConditionSchema: z.ZodType<DeliveryDaysCondition> =
  DeliveryDaysConditionSchema_;
export interface DeliveryDaysCondition
  extends z.infer<typeof DeliveryDaysConditionSchema_> {}

const CustomDynamicRateSelectionConditionSchema_ = z.discriminatedUnion(
  "kind",
  [DeliveryDaysConditionSchema_],
);
export const CustomDynamicRateSelectionConditionSchema: z.ZodType<CustomDynamicRateSelectionCondition> =
  CustomDynamicRateSelectionConditionSchema_;
export interface CustomDynamicRateSelectionCondition
  extends z.infer<typeof CustomDynamicRateSelectionConditionSchema_> {}

const CustomDynamicRateSelectionConfigSchema_ = z.object({
  orderedConditions: z.array(CustomDynamicRateSelectionConditionSchema_),
  fallbackSelectionMethod: createEnum(DynamicRateSelectionMethod, {
    enumName: "DynamicRateSelectionMethod",
  }),
});
export const CustomDynamicRateSelectionConfigSchema: z.ZodType<CustomDynamicRateSelectionConfig> =
  CustomDynamicRateSelectionConfigSchema_;
export interface CustomDynamicRateSelectionConfig
  extends z.infer<typeof CustomDynamicRateSelectionConfigSchema_> {}

export const DEFAULT_CUSTOM_RATE_SELECTION_CONFIG: CustomDynamicRateSelectionConfig =
  {
    orderedConditions: [],
    fallbackSelectionMethod: DynamicRateSelectionMethod.CHEAPEST,
  } as const;

const BaseRateSchema = z.object({
  enabled: z.boolean().optional(),
  merchantPaidCoverageTypes: z
    .object({
      return: z.boolean().optional(),
      packageProtection: z.boolean().optional(),
    })
    .optional(),
});

const BaseShippingRateTableSchema = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  description: z.string(),
  code: z.string(),
  type: createEnum(RateTableType, { enumName: "RateTableType" }),
  originLocations: z.array(ShippingRateLocationSchema),
  destinationLocations: z.array(ShippingRateLocationSchema),
  coverageTypes: z
    .object({
      return: z.boolean().optional(),
      packageProtection: z.boolean().optional(),
    })
    .optional(),
  orderTags: z.array(z.string()).optional(),
  shipmentTagIds: z.array(z.string()).nullish(),
  shippingDiscountRules: z.array(shippingDiscountRuleSchema).optional(),
  deliveryProfile: z.object({ id: z.string(), name: z.string() }).optional(),
  priceConstraints: PriceConstraintSchema.nullish(),
});

const FixedRateSchema = BaseRateSchema.extend({
  price: z.string(),
  conditions: z.array(RateConditionSchema),
  currency: z.string(),
});
export type FixedRate = z.infer<typeof FixedRateSchema>;

const FixedShippingRateTableSchema = BaseShippingRateTableSchema.extend({
  type: z.literal(RateTableType.FIXED),
  rates: z.array(FixedRateSchema).min(1),
});
export type FixedShippingRateTable = z.infer<
  typeof FixedShippingRateTableSchema
>;
const DisplayedFixedShippingRateTableSchema = FixedShippingRateTableSchema.omit(
  {
    _id: true,
    originLocations: true,
    destinationLocations: true,
    coverageTypes: true,
    code: true,
  },
).extend({ id: z.string() });
export type DisplayedFixedShippingRateTable = z.infer<
  typeof DisplayedFixedShippingRateTableSchema
>;

export const carrierAndServiceSchema = z.union([
  z.object({
    carrier: z.literal(Carriers.FEDEX),
    service: createEnum(FedexServices, { enumName: "FedexServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.UPS),
    service: createEnum(UpsServices, { enumName: "UpsServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.USPS),
    service: createEnum(UspsServices, { enumName: "UspsServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.AMAZON),
    service: createEnum(AmazonServices, { enumName: "AmazonServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.DHL_EXPRESS),
    service: createEnum(DhlExpressServices, { enumName: "DhlExpressServices" }),
  }),
  z.object({
    carrier: z.literal(Carriers.DHL_ECS),
    service: createEnum(DhlEcsServices, { enumName: "DhlEcsServices" }),
  }),
]);
export type CarrierAndService = z.infer<typeof carrierAndServiceSchema>;

/**
 * When we paired carriers and services, we decided to include services from
 * multiple carriers in the same service enum. For example, the `FedexServices`
 * enum includes services from both FedEx and FedExSmartPost carriers.
 *
 * As a quick fix to having to map from every carrier to every service (which
 * is likely the ideal solution), we consider certain carriers to be
 * equivalent. If we end up creating the ideal mapping, we need to write a
 * migration to update the carrier/service pairs saved in the database for
 * rateTables in checkout experiences to be correct.
 */
export const carrierFromCarrierAndServiceToEquivalentAllowedCarriersForCheckoutRates: Record<
  CarrierAndService["carrier"],
  Set<Carriers>
> = {
  [Carriers.FEDEX]: new Set([Carriers.FEDEX, Carriers.FEDEX_SMART_POST]),
  [Carriers.UPS]: new Set([Carriers.UPS, Carriers.UPS_SUREPOST]),
  [Carriers.USPS]: new Set([Carriers.USPS]),
  [Carriers.AMAZON]: new Set([Carriers.AMAZON]),
  [Carriers.DHL_EXPRESS]: new Set([Carriers.DHL_EXPRESS]),
  [Carriers.DHL_ECS]: new Set([Carriers.DHL_ECS]),
};

const DynamicRateSchema = BaseRateSchema.extend({
  markup: z
    .object({
      amount: z.string().optional(),
      percentage: z.string().optional(),
    })
    .optional(),
  carrierService: carrierAndServiceSchema,
});
export type DynamicRate = z.infer<typeof DynamicRateSchema>;

const DynamicShippingRateTableSchema = BaseShippingRateTableSchema.extend({
  type: z.literal(RateTableType.DYNAMIC),
  serviceLevelBucketedServiceSelection: createEnum(ServiceLevel, {
    enumName: "ServiceLevel",
  }).optional(),
  markup: z
    .object({
      amount: z
        .object({
          currency: createEnum(Currency, { enumName: "Currency" }),
          value: z.string(),
        })
        .optional(),
      percentage: z.string().optional(),
    })
    .optional(),
  rounding: createEnum(DynamicRateRounding, {
    enumName: "DynamicRateRounding",
  }).nullish(),
  rates: z.array(DynamicRateSchema),
  multipleDynamicRateSelection: createEnum(DynamicRateSelectionPreset, {
    enumName: "DynamicRateSelectionPreset",
  }).optional(),
  /**
   * Custom rate selection config used when multipleDynamicRateSelection is
   * CUSTOM.
   */
  customDynamicRateSelectionConfig:
    CustomDynamicRateSelectionConfigSchema.optional(),
  fulfillmentDelayDaysOverride: z.number().optional(),
  conditions: z.array(RateConditionSchema).optional(),
  excludedCarriers: z
    .array(createEnum(Carriers, { enumName: "Carriers" }))
    .optional(),
  /**
   * If true, when this rate is purchased, the Temporal activity to apply the
   * checkout rate purchased will be skipped when the fulfillment group is
   * processed into the Redo OMS.
   */
  skipOmsApplyCheckoutRatePurchased: z.boolean().optional(),
  priceBounds: z
    .object({
      currency: createEnum(Currency, { enumName: "Currency" }),
      minimum: z.string().optional(),
      maximum: z.string().optional(),
    })
    .refine((data) => isDef(data.minimum) || isDef(data.maximum), {
      message:
        "priceBounds should not exist if neither minimum or maximum is defined.",
    })
    .refine(
      (data) => {
        if (isDef(data.minimum) && isDef(data.maximum)) {
          return Number(data.minimum) <= Number(data.maximum);
        }

        return true;
      },
      {
        message: "Minimum price must be less than maximum price if both exist.",
      },
    )
    .optional(),
  rateTableTextFormat: rateTableTextFormatSchema.optional(),
}).refine(
  (data) => {
    // When multipleDynamicRateSelection is CUSTOM, customDynamicRateSelectionConfig must be present
    if (
      data.multipleDynamicRateSelection === DynamicRateSelectionPreset.CUSTOM
    ) {
      return isDef(data.customDynamicRateSelectionConfig);
    }
    return true;
  },
  {
    message:
      "customDynamicRateSelectionConfig is required when multipleDynamicRateSelection is CUSTOM",
    path: ["customDynamicRateSelectionConfig"],
  },
);
export type DynamicShippingRateTable = z.infer<
  typeof DynamicShippingRateTableSchema
>;
const DisplayedDynamicShippingRateTableSchema =
  DynamicShippingRateTableSchema.omit({
    _id: true,
    originLocations: true,
    destinationLocations: true,
    coverageTypes: true,
    code: true,
  }).extend({ id: z.string() });
export type DisplayedDynamicShippingRateTable = z.infer<
  typeof DisplayedDynamicShippingRateTableSchema
>;

export const ShippingRateTableSchema = z.discriminatedUnion("type", [
  FixedShippingRateTableSchema,
  DynamicShippingRateTableSchema,
]);
export type ShippingRateTable = z.infer<typeof ShippingRateTableSchema>;

export const DisplayedShippingRateTableSchema = z.discriminatedUnion("type", [
  DisplayedFixedShippingRateTableSchema,
  DisplayedDynamicShippingRateTableSchema,
]);
export type DisplayedShippingRateTable = z.infer<
  typeof DisplayedShippingRateTableSchema
>;

export const ShippingRateTableArraySchema = z.array(ShippingRateTableSchema);

const CreateFixedShippingRateTableInputSchema =
  FixedShippingRateTableSchema.omit({
    _id: true,
    code: true,
    originLocations: true,
    destinationLocations: true,
  });
export type CreateFixedShippingRateTableInput = z.infer<
  typeof CreateFixedShippingRateTableInputSchema
>;

const CreateDynamicShippingRateTableInputSchema =
  DynamicShippingRateTableSchema.omit({
    _id: true,
    code: true,
    originLocations: true,
    destinationLocations: true,
  });
export type CreateDynamicShippingRateTableInput = z.infer<
  typeof CreateDynamicShippingRateTableInputSchema
>;

export const CreateShippingRateTableInputSchema = z.discriminatedUnion("type", [
  CreateFixedShippingRateTableInputSchema,
  CreateDynamicShippingRateTableInputSchema,
]);
export type CreateShippingRateTableInput = z.infer<
  typeof CreateShippingRateTableInputSchema
>;
export interface ICreateShippingRateTableInput
  extends z.ZodType<CreateShippingRateTableInput> {}

export function isFixedRateTable(
  rateTable: ShippingRateTable,
): rateTable is FixedShippingRateTable {
  return rateTable.type === RateTableType.FIXED;
}

export function isDisplayedFixedRateTable(
  rateTable: DisplayedShippingRateTable,
): rateTable is DisplayedFixedShippingRateTable {
  return rateTable.type === RateTableType.FIXED;
}

export function isDynamicRateTable(
  rateTable: ShippingRateTable,
): rateTable is DynamicShippingRateTable {
  return rateTable.type === RateTableType.DYNAMIC;
}

export function isDisplayedDynamicRateTable(
  rateTable: DisplayedShippingRateTable,
): rateTable is DisplayedDynamicShippingRateTable {
  return rateTable.type === RateTableType.DYNAMIC;
}

export function isFixedRate(
  rate: ShippingRateTable["rates"][number],
): rate is FixedRate {
  return "price" in rate;
}

export function isDynamicRate(
  rate: ShippingRateTable["rates"][number],
): rate is DynamicRate {
  return "markup" in rate;
}

export function isWeightCondition(
  condition: RateCondition,
): condition is WeightCondition {
  return condition.type === ConditionalPricingCondition.WEIGHT;
}

export function isPriceCondition(
  condition: RateCondition,
): condition is PriceCondition {
  return condition.type === ConditionalPricingCondition.PRICE;
}

export function evaluateRateTableConditions({
  initialConditions,
  order,
  evaluatePriceConditionsOnPostDiscountOrderValue,
  exchangeRates,
}: {
  initialConditions: RateCondition[];
  order: { totalPricePresentment: Money; totalGrams: number };
  evaluatePriceConditionsOnPostDiscountOrderValue: boolean;
  exchangeRates: CurrencyConverter;
}): boolean {
  const conditions = initialConditions.map((condition) => {
    if (condition.type === ConditionalPricingCondition.PRICE) {
      return {
        ...condition,
        exchangeRate: order.totalPricePresentment.currency
          ? exchangeRates.exchangeRate(
              order.totalPricePresentment.currency,
              condition.currency as Currency,
            )
          : 1,
      };
    }
    return condition;
  });

  const matchesConditions = doesRateMatchConditions({
    conditions,
    evaluatePriceConditionsOnPostDiscountOrderValue,
    orderTotalValue: order.totalPricePresentment.amount,
    orderTotalWeightInGrams: order.totalGrams,
  });

  return matchesConditions;
}

export function doesRateMatchConditions({
  conditions,
  evaluatePriceConditionsOnPostDiscountOrderValue,
  orderTotalValue,
  orderTotalWeightInGrams,
}: {
  conditions: FixedShippingRateConditionWithExchangeRate[];
  evaluatePriceConditionsOnPostDiscountOrderValue: boolean;
  orderTotalValue: number;
  orderTotalWeightInGrams: number;
}) {
  let ratePassesAllConditions = true;

  for (const condition of conditions) {
    let conditionPassed = false;
    switch (condition.type) {
      case ConditionalPricingCondition.PRICE: {
        const priceAfterExchangeRate = orderTotalValue * condition.exchangeRate;
        conditionPassed =
          priceAfterExchangeRate >= condition.minPrice &&
          priceAfterExchangeRate < condition.maxPrice;
        break;
      }
      case ConditionalPricingCondition.WEIGHT:
        conditionPassed =
          orderTotalWeightInGrams >= condition.minGrams &&
          orderTotalWeightInGrams < condition.maxGrams;
        break;
      default:
        assertNever(condition);
    }

    if (!conditionPassed) {
      ratePassesAllConditions = false;
      break;
    }
  }

  return ratePassesAllConditions;
}

type FixedShippingRateConditionWithExchangeRate =
  | (Extract<
      FixedShippingRateTable["rates"][number]["conditions"][number],
      { type: ConditionalPricingCondition.PRICE }
    > & { exchangeRate: number })
  | Exclude<
      FixedShippingRateTable["rates"][number]["conditions"][number],
      { type: ConditionalPricingCondition.PRICE }
    >;
